import { useState } from "react";
import { Dialog, DialogTrigger, Pressable } from "react-aria-components";
import { Clock3, Info, Loader, TriangleAlert, X } from "lucide-react";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";
import { cn } from "@/utils/helpers";
import type { ReferenceSubmission } from "@/types/reference.type";
import { useDismissSubmission, useRetrySubmission } from "../api/useReferences";
import { isTerminalSubmission } from "../api/referencesPolling";

/**
 * The activity disclosure.
 *
 * This used to be a permanent feed above the wall. It is now ONE line in the
 * filter bar that expands to the rows, and the line does not exist at all when
 * the queue has nothing to say: a quiet queue takes no space on a wall.
 *
 * "Nothing to say" is deliberately not the same as "nothing is running". A
 * submission that failed is terminal, and so is one that saved two links and
 * refused a third, and both of those are exactly the things a person still has
 * to see. So the line exists while there is either work in flight OR an
 * unresolved answer nobody has dismissed, and it disappears when neither is
 * true. Dismissing is how a person says they have read it, which is the only
 * signal the API offers for that.
 */

function preview(text: string): string {
  const line = text.trim().split("\n")[0];
  return line.length > 90 ? `${line.slice(0, 90)}…` : line;
}

/** A terminal submission that a person still has to look at: it produced
 *  nothing, or it produced something and refused part of the input. */
function needsAttention(submission: ReferenceSubmission): boolean {
  return submission.status === "failed" || submission.skipped.length > 0;
}

export interface SubmissionActivityState {
  /** Queued or processing right now. */
  working: number;
  /** Finished, and carrying a failure or a skip reason nobody has dismissed. */
  attention: number;
  rows: ReferenceSubmission[];
}

export function submissionActivity(submissions: ReferenceSubmission[]): SubmissionActivityState {
  const working = submissions.filter((s) => !isTerminalSubmission(s));
  const attention = submissions.filter((s) => isTerminalSubmission(s) && needsAttention(s));
  return {
    working: working.length,
    attention: attention.length,
    /* In-flight work first: it is the thing that changes on its own, and a row
       that resolves moves down the list rather than out of it. */
    rows: [...working, ...attention],
  };
}

/** What the one line says. Both halves when both are true, because "2
 *  processing" over a failed paste would hide the failure behind progress. */
export function activityLabel(state: SubmissionActivityState): string | null {
  const parts: string[] = [];
  if (state.working > 0) parts.push(`${state.working} processing`);
  if (state.attention > 0) parts.push(`${state.attention} needs a look`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * References / Activity · Trigger and Popover (library, style B). The trigger
 * sits in the title row and says what is processing and, in red, what needs a
 * look; it renders nothing when the queue is quiet. It opens the popover in
 * chrome frost, radius 12, 460 wide: ACTIVITY · N, then one row per save with
 * its state, Retry and Dismiss.
 */
export default function SubmissionActivity({
  state,
  canEdit,
}: {
  state: SubmissionActivityState;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (state.working === 0 && state.attention === 0) return null;

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Pressable>
        <button type="button" className="ref-activity-trigger" aria-label={`Activity: ${activityLabel(state)}`}>
          {state.attention > 0 ? <span className="ref-activity-dot" aria-hidden /> : <Loader className="size-3.5 animate-spin text-co-muted" aria-hidden />}
          {state.working > 0 ? (
            <span className="text-co-text2">
              {state.working} processing{state.attention > 0 ? " ·" : ""}
            </span>
          ) : null}
          {state.attention > 0 ? <span className="text-co-danger">{state.attention} needs a look</span> : null}
        </button>
      </Pressable>
      <KitPopover placement="bottom end" className="ref-chrome-pop w-[460px] max-w-[calc(100vw-2rem)] p-0">
        <Dialog aria-label="Activity" className="flex flex-col gap-1 px-4 py-3 outline-none">
          <p className="ref-mono-label text-[10.5px] tracking-[1.2px]">Activity · {state.rows.length}</p>
          <ul className="flex flex-col">
            {state.rows.map((submission) => (
              <ActivityRow key={submission.id} submission={submission} canEdit={canEdit} />
            ))}
          </ul>
        </Dialog>
      </KitPopover>
    </DialogTrigger>
  );
}

/**
 * A skip reason as the person reads it. The backend writes readable reasons
 * now; a row saved before that may still carry a scraper's or a model's raw
 * error (JSON, a stack, a status dump), which never reaches the page.
 */
function readableSkipReason(reason: string): string {
  const raw = reason.trim();
  if (!raw || raw.length > 140 || /[{}[\]<>\n]|\bError\b|\bHTTP\b/.test(raw)) return "It couldn't be analyzed";
  return raw;
}

function ActivityRow({
  submission,
  canEdit,
}: {
  submission: ReferenceSubmission;
  canEdit: boolean;
}) {
  const retry = useRetrySubmission();
  const dismiss = useDismissSubmission();

  const failed = submission.status === "failed";
  const working = !isTerminalSubmission(submission);
  const Icon = failed ? TriangleAlert : working ? (submission.status === "queued" ? Clock3 : Loader) : Info;
  const title = working
    ? submission.status === "queued"
      ? "Waiting for the analyzer"
      : "Being analyzed"
    : failed
      ? /* Never the backend's own error: it is written for us, not for the person. */
        "Nothing could be saved."
      : `Saved, and ${submission.skipped.length} skipped`;
  const source = submission.rawInput.trim() || submission.uploads?.map((u) => u.fileName).join(", ") || "";

  return (
    <li className="flex items-center gap-3 px-1 py-2.5">
      <Icon aria-hidden className={cn("size-[15px] shrink-0", failed ? "text-co-danger" : "text-co-muted", working && submission.status !== "queued" && "animate-spin")} />
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <p className={cn("text-[13.5px] font-medium", failed ? "text-co-danger" : "text-co-text")}>{title}</p>
        {source ? <p className="ref-mono-faint truncate">{preview(source.replace(/^https?:\/\/(www\.)?/, ""))}</p> : null}
        {submission.skipped.map((skip, i) => (
          <p key={`${skip.input}-${i}`} className="text-[12px] leading-[1.45] text-co-muted">
            {readableSkipReason(skip.reason)}: <span className="break-all text-co-faint">{preview(skip.input)}</span>
          </p>
        ))}
      </div>
      {canEdit && !working ? (
        <div className="flex shrink-0 items-center gap-1">
          {failed ? (
            <button type="button" className="ref-text-action" disabled={retry.isPending} onClick={() => retry.mutate(submission.id)}>
              Retry
            </button>
          ) : null}
          <button
            type="button"
            aria-label={`Dismiss ${preview(source)}`}
            onClick={() => dismiss.mutate(submission.id)}
            className="grid size-[30px] place-items-center rounded-full text-co-muted transition-colors hover:bg-co-hover hover:text-co-text"
          >
            <X aria-hidden className="size-[15px]" />
          </button>
        </div>
      ) : null}
    </li>
  );
}
